/**
 * Types related to promotion packets
 */

import { PacketStatus, PacketVisibility, Decision, Ratification } from './enums';
import type { User } from './user';

/**
 * Valid status transitions for a promotion packet
 */
export const PACKET_STATUS_TRANSITIONS: Record<PacketStatus, PacketStatus[]> = {
  [PacketStatus.DRAFT]: [PacketStatus.BLINDED_ASSESSMENT, PacketStatus.WITHDRAWN],
  [PacketStatus.BLINDED_ASSESSMENT]: [PacketStatus.UNBLINDED_ASSESSMENT, PacketStatus.WITHDRAWN],
  [PacketStatus.UNBLINDED_ASSESSMENT]: [PacketStatus.MANAGER_SUMMARY, PacketStatus.WITHDRAWN],
  [PacketStatus.MANAGER_SUMMARY]: [PacketStatus.SENIOR_LEADER_REVIEW, PacketStatus.WITHDRAWN],
  [PacketStatus.SENIOR_LEADER_REVIEW]: [PacketStatus.HR_REVIEW, PacketStatus.WITHDRAWN],
  [PacketStatus.HR_REVIEW]: [PacketStatus.COMPLETED, PacketStatus.WITHDRAWN],
  [PacketStatus.COMPLETED]: [PacketStatus.WITHDRAWN],
  [PacketStatus.WITHDRAWN]: [],
};

export function isPacketSupportingContentEditable(status: PacketStatus): boolean {
  return status !== PacketStatus.COMPLETED && status !== PacketStatus.WITHDRAWN;
}

/**
 * Represents the main Promotion Packet data structure
 */
export interface Packet {
  id: string; // Prefixed ID, e.g., pkt_...
  year: string; // Assessment year, e.g., "2024"
  candidateId: string; // User ID
  managerId: string; // User ID
  managerIds?: string[]; // Primary manager plus packet-scoped co-managers included in this response
  seniorLeaderId: string; // User ID
  currentTitle: string;
  targetTitle: string;
  timeInRole: string; // Pre-calculated string, e.g., "1.5 years"
  lastPromotedDate?: Date | null; // Date object
  status: PacketStatus;
  visibility: PacketVisibility;
  summary?: string | null; // New summary field
  recommendation?: Decision | null; // Assessor-majority recommendation before ratification
  decision?: Decision | null; // Use Decision enum
  decisionJustification?: string | null;
  ratification?: Ratification | null; // Use Ratification enum
  overruleJustification?: string | null;
  ratificationTimestamp?: Date | null; // Date object
  withdrawalJustification?: string | null; // Justification for withdrawal
  createdAt: Date; // Date object
  updatedAt: Date; // Date object

  /**
   * Attempt number within the same assessment year (1 = first attempt)
   */
  attempt?: number;

  /**
   * ID of the first packet in the same assessment year (null for the first attempt)
   */
  parentPacketId?: string | null;

  /**
   * Convenience flag indicating whether this packet is the most recent attempt for the candidate in the given year
   */
  isLatestAttempt?: boolean;
}

/**
 * Packet object enriched with candidate display name and initials,
 * used for frontend packet detail views including blinded/unblinded redaction.
 */
export interface PacketWithCandidate extends Packet {
  /** Display name for candidate or neutral label */
  candidateName: string;
  /** Display initials for avatar or neutral initials */
  candidateInitials: string;
  /** Packet-scoped assessors for list/export views */
  assessors?: Array<Pick<User, 'id' | 'email' | 'name'>>;
}

export const PACKET_VALIDATION = {
  MAX_SUMMARY_LENGTH: 5000,
  MAX_OVERRULE_JUSTIFICATION_LENGTH: 5000,
  MAX_WITHDRAWAL_JUSTIFICATION_LENGTH: 5000,
} as const;
